// Ported from vlookup-dramatic/components/app-phone.tsx by scripts/revamp/port-design.js
import React from "react";
import Image from "./image";
import { Bell, Calendar, ChevronRight, House, MessageCircle, Search, UserRound, Video, } from "../lib/icons";
import { featuredMentors } from "../lib/content";
import { cn } from "../lib/utils";
/* A hand-built vLookUp app mock — deliberately NOT a store screenshot.
   Everything is markup, so it inherits the brand tokens: the app chrome uses
   brand-blue (the only large blue surface allowed, see globals.css v5 note),
   the primary action uses brand-red, and no cream/navy slabs appear. */
const TABS = [
    { icon: House, label: "Home", active: true },
    { icon: Search, label: "Mentors", active: false },
    { icon: Calendar, label: "Sessions", active: false },
    { icon: MessageCircle, label: "Chat", active: false },
    { icon: UserRound, label: "You", active: false },
];
export function AppPhone({ className }) {
    const mentors = featuredMentors.slice(0, 3);
    // The mock is drawn at a fixed 300px because every size inside it is in px.
    // A narrow phone only leaves (viewport − 112px) of panel content width, and
    // letting the device reflow into that squeezes the mentor rows into
    // ellipses. `zoom` scales the whole device as one unit — layout box
    // included — so the proportions and type sizes survive. max-w-full stays as
    // a last-resort guard against overflow on anything narrower still.
    return (<div className={cn("relative w-[300px] max-w-full max-[430px]:[zoom:0.9] max-[380px]:[zoom:0.8] max-[350px]:[zoom:0.68]", className)}>
      {/* device body */}
      <div className="relative rounded-[2.75rem] bg-brand-ink p-2.5 shadow-[0_40px_80px_-32px_rgba(42,109,251,0.55)] ring-1 ring-brand-navy/15">
        <div className="relative overflow-hidden rounded-[2.15rem] bg-card">
          {/* ── app chrome (brand blue) ─────────────────────────────── */}
          <div className="relative bg-brand-blue px-5 pb-14 pt-2.5 text-white">
            {/* status bar */}
            <div className="flex items-center justify-between text-[10px] font-semibold tabular-nums">
              <span>9:41</span>
              <span className="flex items-center gap-1" aria-hidden>
                <SignalGlyph />
                <WifiGlyph />
                <BatteryGlyph />
              </span>
            </div>

            {/* dynamic island */}
            <div aria-hidden className="absolute left-1/2 top-1.5 h-[22px] w-[86px] -translate-x-1/2 rounded-full bg-brand-ink"/>

            <div className="mt-5 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-medium text-white/70">
                  Good morning
                </div>
                <div className="font-display text-lg font-semibold leading-tight">
                  Sangeetha
                </div>
              </div>
              <span className="relative flex size-8 items-center justify-center rounded-full bg-white/15">
                <Bell className="size-4"/>
                <span className="absolute right-1.5 top-1.5 size-1.5 rounded-full bg-brand-red ring-2 ring-brand-blue"/>
              </span>
            </div>
          </div>

          {/* ── next session card, lifted over the blue ─────────────── */}
          {/* relative + z-10: the blue chrome above is positioned, so a static
            card would paint under it and lose its top row. */}
          <div className="relative z-10 -mt-11 px-4">
            <div className="rounded-2xl bg-card p-3.5 shadow-lg ring-1 ring-brand-navy/10">
              <div className="flex items-center justify-between">
                <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-navy/45">
                  Next session
                </span>
                <span className="rounded-full bg-brand-green/10 px-2 py-0.5 text-[9px] font-bold text-brand-green">
                  Confirmed
                </span>
              </div>
              <div className="mt-2.5 flex items-center gap-2.5">
                <span className="relative size-9 shrink-0 overflow-hidden rounded-full ring-1 ring-brand-navy/10">
                  <Image src={mentors[0].img} alt="" fill sizes="36px" className="object-cover"/>
                </span>
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-[12px] font-bold text-brand-navy">
                    {mentors[0].name}
                  </div>
                  <div className="truncate text-[10px] text-muted-foreground">
                    Thu 14 Aug · 4:00 PM
                  </div>
                </div>
                <span className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-full bg-brand-red px-2.5 py-1.5 text-[10px] font-bold text-white">
                  <Video className="size-3"/>
                  Join
                </span>
              </div>
            </div>
          </div>

          {/* ── mentor list ─────────────────────────────────────────── */}
          <div className="px-4 pt-5">
            <div className="flex items-baseline justify-between">
              <span className="text-[9px] font-bold uppercase tracking-[0.14em] text-brand-navy/45">
                Mentors for you
              </span>
              <span className="text-[10px] font-semibold text-brand-blue">
                See all
              </span>
            </div>

            <div className="mt-2.5 space-y-2">
              {mentors.map((m) => (<div key={m.name} className="flex items-center gap-2.5 rounded-2xl bg-cream-deep p-2.5">
                  {/* the dot sits OUTSIDE the clipping circle, or the rounded
                mask would slice it in half */}
                  <span className="relative size-9 shrink-0">
                    <span className="absolute inset-0 overflow-hidden rounded-full ring-1 ring-brand-navy/10">
                      <Image src={m.img} alt="" fill sizes="36px" className="object-cover"/>
                    </span>
                    {m.available && (<span className="absolute -bottom-0.5 -right-0.5 size-2.5 rounded-full bg-brand-green ring-2 ring-cream-deep"/>)}
                  </span>
                  <div className="min-w-0 leading-tight">
                    <div className="truncate text-[11.5px] font-bold text-brand-navy">
                      {m.name}
                    </div>
                    <div className="truncate text-[9.5px] text-muted-foreground">
                      {m.domain} · {m.level}
                    </div>
                  </div>
                  <ChevronRight className="ml-auto size-3.5 shrink-0 text-brand-navy/25"/>
                </div>))}
            </div>
          </div>

          {/* ── tab bar ─────────────────────────────────────────────── */}
          <div className="mt-5 flex items-center justify-between border-t border-brand-navy/8 px-5 pb-4 pt-3">
            {TABS.map((t) => (<span key={t.label} className={cn("flex flex-col items-center gap-1", t.active ? "text-brand-blue" : "text-brand-navy/30")}>
                <t.icon className="size-[18px]" strokeWidth={t.active ? 2.4 : 2}/>
                <span className="text-[7.5px] font-bold uppercase tracking-wide">
                  {t.label}
                </span>
              </span>))}
          </div>
          <div aria-hidden className="mx-auto mb-2 h-1 w-24 rounded-full bg-brand-navy/20"/>
        </div>
      </div>
    </div>);
}
/* status-bar glyphs — tiny enough that lucide's stroke icons read as noise */
function SignalGlyph() {
    return (<svg viewBox="0 0 16 10" className="h-2.5 w-4 fill-white">
      <rect x="0" y="6.5" width="2.6" height="3.5" rx="0.8"/>
      <rect x="4.2" y="4.5" width="2.6" height="5.5" rx="0.8"/>
      <rect x="8.4" y="2.4" width="2.6" height="7.6" rx="0.8"/>
      <rect x="12.6" y="0" width="2.6" height="10" rx="0.8"/>
    </svg>);
}
function WifiGlyph() {
    return (<svg viewBox="0 0 16 12" className="h-2.5 w-3.5 fill-white">
      <path d="M8 11.2 5.6 8.3a3.8 3.8 0 0 1 4.8 0z"/>
      <path d="M8 5.4c-1.6 0-3 .6-4.2 1.6L2.4 5.4A8.6 8.6 0 0 1 8 3.4c2.2 0 4.1.8 5.6 2l-1.4 1.6A6.3 6.3 0 0 0 8 5.4z"/>
      <path d="M8 1.4c-2.8 0-5.3 1-7.2 2.7L0 3A11.6 11.6 0 0 1 8 0c3.1 0 5.9 1.2 8 3l-.8 1.1A10.8 10.8 0 0 0 8 1.4z"/>
    </svg>);
}
function BatteryGlyph() {
    return (<svg viewBox="0 0 26 12" className="h-2.5 w-[22px]">
      <rect x="0.6" y="0.6" width="21" height="10.8" rx="3" className="fill-none stroke-white/50" strokeWidth="1"/>
      <rect x="2.2" y="2.2" width="15" height="7.6" rx="1.8" className="fill-white"/>
      <path d="M23.4 4.2v3.6a2.1 2.1 0 0 0 0-3.6z" className="fill-white/50"/>
    </svg>);
}
